import React, { useState, useEffect, useMemo } from 'react';
import { SetMetaData } from './../../helper/page-title';
import useInfiniteList from '../../hooks/infinite-list';
import useColumnSettings from '../../hooks/column-setting';
import InfiniteListWrapper from '../../components/wrapper/infinite-list';
import FilterInput from '../../components/form/filter/filter-input';
import normalizeFilters from '../../helper/filter/normalize-filter';
import { SettingDropdownUi, RowDataUi } from '../../components/form/master-table/ui-static';
import ValidationHelper from '../../helper/validation';
import {
    post_api_call,
    get_api_call,
    error_api_call,
} from './../../actions/action-creators/common';
import { alertResponseMessage } from '../../actions/action-creators/response';
import ModalMaster from '../../modal/master';
import ModalMasterDetail from '../../modal/master-detail';
import FilterFloatMaster from "../../components/form/filter/filter-float";
// Form data
import FormDataAuditor from '../../data/formData/auditor/auditor';
import { activeStatusOpp, genderOpp } from '../../data/formData/option';

const {
    masterTableFields,
    fieldNewAuditor,
    fieldValuesAuditor,
    fieldValidationAuditor,
    fieldValidEditAuditor,
    fieldEditAuditor,
    fieldAuditorView,
    filterTableData
} = FormDataAuditor;

const masterTitle = {
    new:    'New Auditor Entry',
    edit:   'Edit Auditor',
    detail: 'Auditor Details',
};

function AuditorList(props) {
    SetMetaData('Manage Auditors', '', '');

    // ── State ──────────────────────────────────────────────────────────────
    const [filterData, setFilterData]         = useState({});
    const [tmpFilterData, setTmpFilterData]   = useState({});
    const [modalLoader, setModalLoader]       = useState({ edit: false, detail: false });
    const [modalToggle, setModalToggle]       = useState({ new: false, edit: false, detail: false });
    const [masterFieldData, setMasterFieldData] = useState([]);
    const [activeEditForm, setActiveForm]     = useState('');
    const [errorData, setErrorData]           = useState({ new: [], edit: [] });
    const [formNewData, setNewFormData]       = useState({});
    const [formEditData, setEditFormData]     = useState({});
    const [formViewData, setViewFormData]     = useState([]);
    const [formUpdatedData, setUpdatedFormData] = useState({});
    const [isProcess, setIsProcess]           = useState({ new: false, edit: false });
    const [refreshKey, setRefreshKey]         = useState(0);
    const [detailedOppData, setDetailedOppData] = useState({});

    // ── Column settings ────────────────────────────────────────────────────
    const { columnSettings, updateColumn } = useColumnSettings({
        id:                     true,
        name:                   true,
        email:                  true,
        mobile_number:          true,
        dob:                    true,
        gender:                 true,
        college:                true,
        grade_id:               true,
        subject_specialisation: true,
        internship_start_date:  true,
        slot_availability_label:       true,
        active_status_id:       true,
    });

    const [dropdownOpt, setDropdownOpt] = useState({
        gender_id            : genderOpp,
        active_status_id     : activeStatusOpp,
        grade_id             : [],
        slot_availability_ids : [],
        mobile_code_id       : []
    });

    // ── Normalise filters for API ──────────────────────────────────────────
    const normalizedFilter = useMemo(
        () => normalizeFilters(filterData, { 
            active_status_id: 'id',
            slot_availability_ids: 'id',
            grade_id: ''
        }),
        [filterData]
    );

    // ── Infinite list (same hook as stakeholder screen) ────────────────────
    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading,
    } = useInfiniteList({
        queryKey: ['manage_auditors', refreshKey],
        apiCall:  payload => post_api_call('auditors', payload),
        limit:    100,
        extraPayload: {
            fetch_type: 'manage_auditors',
            filter:     normalizedFilter,
        },
    });

    const masterTableData = Array.isArray(data?.pages)
        ? data.pages.flatMap(page => Array.isArray(page?.data) ? page.data : [])
        : [];

    //Get Data from APIs
    const fetchSelectList = async(type, url) => {
      try{
          const selectApiData = await get_api_call(url, {});
          var data = [];
          if(selectApiData.status === 200){
              data = selectApiData.data;
              switch (type) {
                  case 'manage_auditor':
                    setDropdownOpt({...dropdownOpt,
                        grade_id            : data?.auditor_grades,
                        slot_availability_ids: data?.slot_availability,
                        mobile_code_id      : data?.countries
                    });
                    break;
                  default: 
              }
          }

          return {
              data
          };
      }catch(error){
        error_api_call({error: `admin|screens|auditor|list|fetchSelectList|${error}`});
        return {data: []};
      }
    }

    useEffect(() => {
        fetchSelectList('manage_auditor', `dropdown-options/countries,slot_availability,auditor_grades?fetch_type=auditor`);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── View / Edit modal loader ───────────────────────────────────────────
    const loadViewModal = async (e, load_type, row) => {
        e.preventDefault();
        try {
            toggleModal(e, load_type, true, '', load_type);
            setModalLoader({ ...modalLoader, [load_type]: true });

            if (load_type === 'detail') {
                setViewFormData([...fieldAuditorView]);
                setDetailedOppData({ ...row });
            } else {
                // Edit: pre-populate form, convert slot_availability_ids to multi-select objects
                setUpdatedFormData({});
                var selectedSlots = [];
                row.availability_slots.forEach(slot => {
                    selectedSlots.push({id: slot.id, slot: slot.slot_name});
                });

                const genderObj = genderOpp.find(g => g.id === row.gender_id) || '';
                const gradeObj  = dropdownOpt.grade_id.find(y => y.id === row.grade_id) || '';

                setEditFormData({
                    ...fieldValuesAuditor,
                    ...row,
                    gender_id:             genderObj,
                    grade_id:              gradeObj,
                    slot_availability_ids: selectedSlots,
                    // password:        '',  // never pre-fill password
                });
            }
            setModalLoader({ ...modalLoader, [load_type]: false });
        } catch (error) {
            error_api_call({ error: `admin|auditor|list|loadViewModal|${error}` });
            alertResponseMessage({ alert_type: 2, message: 'Error Loading Data' });
        }
    };

    // ── Status toggle ──────────────────────────────────────────────────────
    const approveToggle = async (e, is_active, user_id) => {
        e.preventDefault();
        try {
            if (window.confirm(`${is_active === 1 ? 'Deactivate' : 'Activate'} this Auditor?`)) {
                const targetTag = e.target.tagName.toLowerCase();
                if (targetTag === 'i') e.target.parentElement.disabled = true;
                else                   e.target.disabled = true;

                const res = await post_api_call('auditor/status-change', {
                    id:        user_id,
                    is_active: is_active === 2 ? 1 : 2,
                });

                if (res.status === 200) {
                    alertResponseMessage({
                        alert_type: 1,
                        message: `Auditor ${is_active === 1 ? 'Deactivated' : 'Activated'} Successfully!`,
                    });
                    setRefreshKey(prev => prev + 1);
                }

                if (targetTag === 'i') e.target.parentElement.disabled = false;
                else                   e.target.disabled = false;
            }
        } catch (error) {
            error_api_call({ error: `admin|auditor|list|approveToggle|${error}` });
        }
    };

    // ── Input handler ──────────────────────────────────────────────────────
    const updateInput = (form_type, state_var_type, input_type, field, value) => {
        try {
            if (form_type === 'filter') {
                setTmpFilterData(prev => ({ ...prev, [field]: value }));
                return;
            }
            if (errorData[form_type]?.length) {
                setErrorData(prev => ({ ...prev, [form_type]: [] }));
            }

            // Mobile: digits only, max 10
            if (field === 'mobile_number') {
                if (value.length < 11) value = value.replace(/[^0-9]/g, '');
                else return false;
            } else if (input_type === 'input' || input_type === 'textarea') {
                value = value.trimStart();
                if (field === 'first_name' || field === 'last_name') {
                    value = value.replace(/[^a-zA-Z. ]/g, '').replace('  ', ' ');
                }
            }

            if (form_type === 'new') {
                setNewFormData(prev => ({ ...prev, [field]: value }));
            } else {
                setEditFormData(prev => ({ ...prev, [field]: value }));
                setUpdatedFormData(prev => ({ ...prev, [field]: value }));
            }
        } catch (error) {
            console.error(error);
        }
    };

    // ── Toggle modal ───────────────────────────────────────────────────────
    const toggleModal = (e, modal_type, toggleState, index = '', form_type = '') => {
        try {
            if (toggleState) {
                e.preventDefault();
                switch (form_type) {
                    case 'new':
                        setNewFormData({ ...fieldValuesAuditor });
                        setMasterFieldData([{ form_type: 'new', label: 'Details', fields: [...fieldNewAuditor] }]);
                        break;
                    case 'edit':
                        setMasterFieldData([{ form_type: 'edit', label: 'Details', fields: [...fieldEditAuditor] }]);
                        break;
                    default:
                        break;
                }
                setErrorData(prev => ({ ...prev, [form_type]: [] }));
                setActiveForm(form_type);
            }
            setModalToggle(prev => ({ ...prev, [modal_type]: toggleState }));
        } catch (error) {
            console.error(error);
        }
    };

    // ── Submit (create / update) ───────────────────────────────────────────
    const updateAuditor = async (e, form_type = '') => {
        try {
            e.preventDefault();
            setErrorData(prev => ({ ...prev, [form_type]: [] }));
            setIsProcess(prev => ({ ...prev, [form_type]: true }));

            const inputData       = form_type === 'new' ? { ...formNewData } : { ...formEditData };
            const validationRules = form_type === 'new' ? fieldValidationAuditor : fieldValidEditAuditor;
            const validation      = ValidationHelper.validation(inputData, validationRules).error;

            if (validation.length) {
                setErrorData(prev => ({ ...prev, [form_type]: validation }));
                setIsProcess(prev => ({ ...prev, [form_type]: false }));
                return;
            }

            // ── Build API payload ──────────────────────────────────────────
            const changedData = form_type === 'edit' ? { ...formUpdatedData } : inputData;

            if (form_type === 'edit' && Object.keys(formUpdatedData).length === 0) {
                alertResponseMessage({ alert_type: 3, message: 'Nothing to Update' });
                setIsProcess(prev => ({ ...prev, [form_type]: false }));
                toggleModal('', form_type, false);
                return;
            }

            const apiPayload = {};

            // Scalar text fields — send as-is
            const scalarFields = [
                'first_name',
                'last_name', 
                'email',
                'mobile_number',
                'college',
                'subject_specialisation',
                'upi_id',
                'remarks',
                'dob',
                'internship_start_date',
                'password'
            ];
            scalarFields.forEach(f => {
                if (f in changedData && changedData[f] !== undefined) {
                    apiPayload[f] = changedData[f] || null;
                }
            });

            if ('mobile_code_id' in changedData && changedData.mobile_code_id) {
                apiPayload.mobile_code_id = changedData.mobile_code_id?.id ?? changedData.mobile_code_id;
            }

            // gender_id — select returns object { id, gender }
            if ('gender_id' in changedData && changedData.gender_id) {
                apiPayload.gender_id = changedData.gender_id?.id ?? changedData.gender_id;
            }

            // grade_id — select returns object { id, year }
            if ('grade_id' in changedData && changedData.grade_id) {
                apiPayload.grade_id = changedData.grade_id?.id ?? changedData.grade_id;
            }

            // slot_availability_ids — multi-select returns array of objects [{ id, slot }]
            if ('slot_availability_ids' in changedData && Array.isArray(changedData.slot_availability_ids)) {
                apiPayload.slot_availability_ids = changedData.slot_availability_ids.map(s => s?.id ?? s);
            }

            if (form_type === 'new') {
                apiPayload.email = inputData.email;
            } else {
                apiPayload.user_id = formEditData.id;
            }

            const endpoint = form_type === 'new' ? 'auditor/create' : 'auditor/update';
            const res = await post_api_call(endpoint, apiPayload);

            if (res.status === 200) {
                alertResponseMessage({
                    alert_type: 1,
                    message: form_type === 'new'
                        ? 'Auditor Created Successfully'
                        : 'Auditor Updated Successfully',
                });
                setIsProcess(prev => ({ ...prev, [form_type]: false }));
                toggleModal('', form_type, false);
                setRefreshKey(prev => prev + 1);
            } else {
                setIsProcess(prev => ({ ...prev, [form_type]: false }));
                if (res.message) {
                    setErrorData(prev => ({ ...prev, [form_type]: [res.message] }));
                }
            }
        } catch (error) {
            console.error(error);
            setErrorData(prev => ({
                ...prev,
                [form_type]: ['Something Went Wrong, Please Try After Some Time'],
            }));
            setIsProcess(prev => ({ ...prev, [form_type]: false }));
            error_api_call({ error: `admin|auditor|list|updateAuditor|${error}` });
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────────────────────
    // All select options for ModalMaster — mirrors stakeholder list pattern.
    const selectOptions = {
        gender_id           : { value: 'id', label: 'gender',  data: [...genderOpp] },
        grade_id            : { value: 'id', label: 'class_name', data: [...dropdownOpt.grade_id] },
        slot_availability_ids: { value: 'id', label: 'slot', data: [...dropdownOpt.slot_availability_ids] },
        active_status_id    : { value: 'id', label: 'status', data: [...activeStatusOpp] },
        mobile_code_id      : { value: 'id', label: 'phone_code', data: [...dropdownOpt.mobile_code_id] },
    };

    return (
        <>
            <div className="row">
                {/* Add button */}
                <div className="col-lg-12 text-end">
                    <button
                        className="btn btn-info mb-2"
                        onClick={e => toggleModal(e, 'new', true, '', 'new')}
                    >
                        Add New Auditor
                    </button>
                </div>

                {/* Filter Res  */}
                <div className="col-lg-12">
                    <div className="card p-2 mb-2">
                        {/* Filter by: */}
                        <FilterFloatMaster
                            title="Filter Auditors"
                            formType="filter"
                            data={tmpFilterData}
                            _isProcess={{...isProcess}}
                            _updateInput= {(form_type, variable_type, input_type, field, value) => updateInput(form_type, variable_type, input_type, field, value)}
                            _formData={[{label: "Filter Auditors:", display_type: 'col', fields: [...filterTableData]}]}
                            _submitForm = {(e, form_type) => {e.preventDefault(); setFilterData({...tmpFilterData})}}
                            _is_download={false}
                            // userRoleIds={props.userRoleIds}
                            selectOptions={{
                                        grade_id: {value: 'id',
                                                    label: 'class_name',
                                                    data: [...dropdownOpt.grade_id]},
                                        slot_availability_ids: {value: 'id',
                                                    label: 'slot',
                                                    data: [...dropdownOpt.slot_availability_ids]},
                                        active_status_id:  {value: 'id',
                                                    label: 'status',
                                                    data: [...dropdownOpt.active_status_id]},
                                    }}
                            _resetFilterForm = {(e) => {e.preventDefault(); setTmpFilterData({})}}
                        />
                    </div>
                </div>

                {/* Infinite list table */}
                <InfiniteListWrapper
                    dataLength={masterTableData.length}
                    isLoading={isLoading}
                    hasNextPage={hasNextPage}
                    fetchNextPage={fetchNextPage}
                    isFetchingNextPage={isFetchingNextPage}
                    emptyMessage="No Auditors found!"
                >
                    <div className="col-lg-12 grid-margin stretch-card">
                        <div className="card">
                            {/* Column settings dropdown */}
                            <SettingDropdownUi
                                checkedData={columnSettings}
                                fields={masterTableFields}
                                updateColumn={(field, value) => updateColumn(field, value)}
                            />

                            {/* Table */}
                            <div className="card-body table-responsive sticky-table pt-0 pe-0 ps-3">
                                <table className="table">
                                    <thead>
                                        <tr>
                                            {masterTableFields.map((col, i) =>
                                                columnSettings[col.field.name] && (
                                                    <th key={i} className={`w-200p ${col.class_name || ''}`}>
                                                        {col.title}
                                                    </th>
                                                )
                                            )}
                                            <th>Action</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {isLoading ? (
                                            <tr>
                                                <td colSpan="20" className="text-center p-4">
                                                    <img
                                                        alt="Loader"
                                                        src={require('../../assets/images/Image-Loader.gif')}
                                                    />
                                                </td>
                                            </tr>
                                        ) : masterTableData.length > 0 ? (
                                            masterTableData.map((row, index) => (
                                                <tr key={index}>
                                                    {masterTableFields.map((tbl, i) =>
                                                        columnSettings[tbl.field.name] && (
                                                            <td key={i}>
                                                                <RowDataUi
                                                                    fetch_key={tbl.fetch_key}
                                                                    data={row}
                                                                    data_type={tbl.data_type ?? 'text'}
                                                                />
                                                            </td>
                                                        )
                                                    )}
                                                    <td className="d-flex">
                                                        {/* View */}
                                                        <button
                                                            onClick={e => loadViewModal(e, 'detail', row)}
                                                            className="btn btn-sm btn-twitter"
                                                            title="View Details"
                                                        >
                                                            <i className="mdi mdi-eye" />
                                                        </button>
                                                        {/* Activate / Deactivate */}
                                                        <button
                                                            onClick={e => approveToggle(e, row.active_status_id, row.id)}
                                                            className={`btn btn-sm ms-2 ${row.active_status_id === 1 ? 'btn-danger' : 'btn-success'}`}
                                                            title={row.active_status_id === 1 ? 'Deactivate' : 'Activate'}
                                                        >
                                                            <i className="mdi mdi-check-outline" />
                                                        </button>
                                                        {/* Edit */}
                                                        <button
                                                            onClick={e => loadViewModal(e, 'edit', row)}
                                                            className="btn btn-sm ms-2 btn-inverse-info"
                                                            title="Edit"
                                                        >
                                                            <i className="mdi mdi-pencil" />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="20">
                                                    <div className="no-fellow text-center mt-4">
                                                        <p className="pt-4 head">No Auditors Found!</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </InfiniteListWrapper>
            </div>

            {/* ── New Entry Modal ─────────────────────────────────────────── */}
            <ModalMaster
                isShow={modalToggle.new}
                _toggleModal={(e, state) => toggleModal(e, 'new', state)}
                modKey={activeEditForm}
                formType="new"
                data={formNewData}
                title={masterTitle[activeEditForm]}
                _errorData={{ ...errorData }}
                _isProcess={{ ...isProcess }}
                _updateInput={(form_type, variable_type, input_type, field, value) =>
                    updateInput(form_type, variable_type, input_type, field, value)
                }
                _submitForm={(e, form_type) => updateAuditor(e, form_type)}
                _formData={[...masterFieldData]}
                selectOptions={selectOptions}
            />

            {/* ── Detail View Modal ───────────────────────────────────────── */}
            <ModalMasterDetail
                data={detailedOppData}
                isShow={modalToggle.detail}
                _toggleModal={(e, state) => toggleModal(e, 'detail', state)}
                loader={modalLoader.detail}
                _formData={[{ label: 'Auditor Detail', fields: [...formViewData] }]}
                title="Auditor Details"
            />

            {/* ── Edit Modal ──────────────────────────────────────────────── */}
            <ModalMaster
                isShow={modalToggle.edit}
                loader={modalLoader.edit}
                _toggleModal={(e, state) => toggleModal(e, 'edit', state)}
                modKey={activeEditForm}
                formType="edit"
                data={formEditData}
                title={masterTitle[activeEditForm]}
                _errorData={{ ...errorData }}
                _isProcess={{ ...isProcess }}
                _updateInput={(form_type, variable_type, input_type, field, value) =>
                    updateInput(form_type, variable_type, input_type, field, value)
                }
                _submitForm={(e, form_type) => updateAuditor(e, form_type)}
                _formData={[...masterFieldData]}
                selectOptions={selectOptions}
            />
        </>
    );
}

export default AuditorList;